import React from "react";
import {
  Divider,
  HStack,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
} from "@chakra-ui/react";
import ResponsiveFormStack from "../common/ResponsiveFormStack";
import { PreviewDropdownFormControl } from "../common/FormControlComponents";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  PresentationRequest,
  emptyPresentationRequest,
} from "../../types/presentation.types";
import { PresentationForm } from "../PresentationForm";
import { usePresentationsQuery } from "../../hooks/usePresentationsQuery";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface PresentationsProps {
  congressId: number;
  presentations?: PresentationRequest[];
  handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
  setFieldValue: Function;
  isEditMode?: boolean;
  moduleChange?: ModuleChangeStatus;
}

const completeArrayUntilThree = (array: (typeof data)[], data: any): void => {
  if (array.length < 3) {
    array.push(data);
    completeArrayUntilThree(array, data);
  }
};

const Presentations: React.FC<PresentationsProps> = ({
  congressId,
  presentations,
  handleChange,
  setFieldValue,
  isEditMode = false,
  moduleChange = "unchanged",
}) => {
  const { i18n } = useLingui();
  const { forms, topics, extraInfo, areFormsLoading, areTopicsLoading } =
    usePresentationsQuery(congressId, isEditMode);

  const isSoftRequired = React.useMemo(
    () => isEditMode && moduleChange === "enabled",
    [isEditMode, moduleChange],
  );

  const hasAnyFieldFilled = React.useMemo(
    () =>
      (presentations?.length ?? 0) > 0 ||
      (presentations ?? []).some(
        (p) =>
          !!(
            (p.presentationTypeId && p.presentationTypeId !== 0) ||
            (p.presentationTopicId && p.presentationTopicId !== 0) ||
            p.presentationTitle?.trim() ||
            p.performers?.trim() ||
            p.performersWorkplace?.trim()
          ),
      ),
    [presentations],
  );

  const hasAnyPresentationContentFilled = React.useMemo(
    () =>
      (presentations ?? []).some(
        (p) =>
          !!(
            (p.presentationTypeId && p.presentationTypeId !== 0) ||
            (p.presentationTopicId && p.presentationTopicId !== 0) ||
            p.presentationTitle?.trim() ||
            p.performers?.trim() ||
            p.performersWorkplace?.trim()
          ),
      ),
    [presentations],
  );

  const isCountSoftRequired = isSoftRequired && !hasAnyFieldFilled;
  const isCountHardRequired = !isSoftRequired || hasAnyFieldFilled;
  const isFieldsSoftRequired =
    isSoftRequired && hasAnyFieldFilled && !hasAnyPresentationContentFilled;
  const isFieldsHardRequired = !isFieldsSoftRequired;

  const defaultCountPreviewText = React.useMemo(() => {
    const noneSelected = (presentations?.length ?? 0) === 0;
    if (isSoftRequired && noneSelected) {
      return i18n._("Originally not selected");
    }
    return i18n._("Please select!");
  }, [isSoftRequired, presentations?.length, i18n]);

  const onPresentationChange = React.useCallback(
    (_fieldName: string, value: undefined | number | string) => {
      let _presentations: PresentationRequest[] = [];
      if (value !== undefined) {
        if (presentations) {
          _presentations = [...presentations];
        }
        const _value = parseInt(value + "");
        completeArrayUntilThree(_presentations, emptyPresentationRequest);
        _presentations.splice(_value, 3 - _value);
      }
      setFieldValue("presentations", _presentations);
    },
    [presentations, setFieldValue]
  );
  const selectedPresentationNumber = React.useMemo(
    () => (presentations ? presentations.length : 0),
    [presentations]
  );
  return (
    <Stack>
      <ModuleSectionHeader
        title={<Trans>Presentations</Trans>}
        change={moduleChange}
      />
      <ResponsiveFormStack
        firstElement={
          <PreviewDropdownFormControl
            id={"presentation"}
            label={i18n._("I keep Presentation")}
            values={[
              { id: 0, leftText: i18n._("No") },
              { id: 1, leftText: i18n._("Yes, 1 Presentation") },
              { id: 2, leftText: i18n._("Yes, 2 Presentations") },
              { id: 3, leftText: i18n._("Yes, 3 Presentations") },
            ]}
            fieldName="presentations"
            setFieldValue={onPresentationChange}
            selectedId={selectedPresentationNumber}
            isRequired={isCountHardRequired}
            isSoftRequired={isCountSoftRequired}
            defaultPreviewText={defaultCountPreviewText}
            testId="presentation-dropdown"
          />
        }
      />
      {(presentations || []).map((presentation, index) => (
        <>
          <PresentationForm
            index={index}
            presentation={presentation}
            types={forms}
            topics={topics}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            isRequired={isFieldsHardRequired}
            isSoftRequired={isFieldsSoftRequired}
            key={index}
          />
          {presentations && index !== presentations.length - 1 && (
            <Divider my={{ base: 4 }} key={"divider-" + index} />
          )}
        </>
      ))}
      {presentations &&
        presentations.length &&
        (areFormsLoading || areTopicsLoading) && (
          <>
            <Skeleton height="50px" />
            <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
            <Skeleton height="50px" />
          </>
        )}
      {extraInfo && (
        <HStack>
          <Text fontSize="m" mb="2">
            {extraInfo.additionalText}
          </Text>
        </HStack>
      )}
    </Stack>
  );
};

export default Presentations;
